/**
 * Enum of SendGrid dynamic template IDs for transactional emails.
 * Templates are created and managed in the SendGrid UI.
 *
 * To add a new template:
 * 1. Create the dynamic template in SendGrid UI
 * 2. Add the template ID here with a descriptive name
 */
export enum SendGridTemplates {
  PhoneAppLink = 'd-b975420378b04698a11295261bf1a83c',
  AppraisalOrderNotification = 'd-0f065eec675840ca9fd1ea669441e096',
  DocutechDisclosureNotification = 'd-ce8fc51930c44d5bb5e2c7c3a6fdd750',
  DocutechDisclosureReminder = 'd-48ea48efdcd64710a410f87c824d61ce',
  DocutechClosingDisclosureReminder = 'd-80bf5a8cafa64b73a9e86c9aeb98d681',
  DocutechSendToFulfillment = 'd-eefe0c6ae7534ff19787e2486048d291',
  DocutechScanAtFulfillment = 'd-f7e7a084d7f141d1bea5ba3f6dbf14f2',
  DocutechPackageOpenedLoNotification = 'd-1a05ab769327467e8405b55c2f41aa3c',
}

// Human-readable titles for each SendGrid template, keyed by template id.
export const SendGridTemplateTitles: Record<SendGridTemplates, string> = {
  [SendGridTemplates.PhoneAppLink]: 'Phone App Link',
  [SendGridTemplates.AppraisalOrderNotification]:
    'Appraisal Order Notification',
  [SendGridTemplates.DocutechDisclosureNotification]: 'Disclosure Notification',
  [SendGridTemplates.DocutechDisclosureReminder]: 'Disclosure Reminder',
  [SendGridTemplates.DocutechClosingDisclosureReminder]:
    'Closing Disclosure Reminder',
  [SendGridTemplates.DocutechSendToFulfillment]: 'Sent to Fulfillment',
  [SendGridTemplates.DocutechScanAtFulfillment]: 'Scan at Fulfillment',
  [SendGridTemplates.DocutechPackageOpenedLoNotification]:
    'Package Opened (Loan Officer)',
}

/**
 * Dynamic template data that can be passed to SendGrid templates.
 * Each template may expect different data fields.
 */
export interface SendGridTemplateData {
  [key: string]: string | number | boolean | object | undefined
}

/**
 * Recipient information for SendGrid emails
 */
export interface SendGridRecipient {
  email: string
  name?: string
}

/**
 * Options for sending a transactional email via SendGrid
 */
export interface SendGridEmailOptions {
  /** Recipient email address or recipient object */
  to: string | SendGridRecipient | SendGridRecipient[]
  /** Email subject (required for non-template emails, optional for dynamic templates) */
  subject?: string
  /** Dynamic template ID from SendGrid */
  templateId?: string
  /** Dynamic template data/variables */
  dynamicTemplateData?: SendGridTemplateData
  /** Plain text content (for non-template emails) */
  text?: string
  /** HTML content (for non-template emails) */
  html?: string
  /** Override the default from email */
  fromEmail?: string
  /** Override the default from name */
  fromName?: string
  /** Reply-to email address */
  replyTo?: string
  /** CC recipients */
  cc?: string | SendGridRecipient | SendGridRecipient[]
  /** BCC recipients */
  bcc?: string | SendGridRecipient | SendGridRecipient[]
  /** Categories for tracking/filtering in SendGrid */
  categories?: string[]
  /** Custom arguments for webhooks */
  customArgs?: Record<string, string>
}

/**
 * Response from SendGrid API after sending an email
 */
export interface SendGridResponse {
  success: boolean
  messageId?: string
  statusCode?: number
  error?: string
}
